import { registerSW } from 'virtual:pwa-register'
import { toast } from '@/design-system'

let updateSW: ((reload?: boolean) => Promise<void>) | null = null

/**
 * ¿Está la persona a mitad de algo que una recarga le haría perder?
 *
 * Antes solo se protegía la cámara, y cada publicación recargaba a quien
 * estuviera editando la ficha de un gato, su perfil o escribiendo en el buzón:
 * lo escrito se perdía. (§H64)
 *   · la cámara, siempre
 *   · una hoja o un diálogo abiertos: solo existen en la página mientras lo
 *     están, así que su presencia basta
 *   · un campo de texto con el cursor dentro
 */
function ocupado(): boolean {
  if (window.location.pathname.startsWith('/capturar')) return true
  if (document.querySelector('[aria-modal="true"]')) return true
  const a = document.activeElement as HTMLElement | null
  if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.tagName === 'SELECT' || a.isContentEditable)) {
    return true
  }
  return false
}

/**
 * Registers the worker and applies new versions at a calm moment, rather than
 * swapping the app out from under someone mid-sentence.
 */
export function registerServiceWorker() {
  if (import.meta.env.DEV && !import.meta.env.VITE_SW_IN_DEV) return

  // La primera vez que se abre la app no había ningún worker al mando, y el
  // primero en tomar el control dispara el mismo aviso que una actualización:
  // eso provocaba una recarga nada más abrirla. Se distingue desde el principio.
  const habiaControlador = Boolean(navigator.serviceWorker?.controller)

  let recargando = false
  let pendiente = false
  let quietoDesde = 0

  const recargar = () => {
    if (recargando) return
    recargando = true
    window.location.reload()
  }

  // Con una versión pendiente, se espera a que la persona lleve unos segundos
  // sin estar a medias de nada: si acaba de pulsar «Guardar», su petición tiene
  // tiempo de llegar antes de recargar.
  const vigilar = () => {
    const reloj = window.setInterval(() => {
      if (!pendiente || recargando) return window.clearInterval(reloj)
      if (ocupado()) {
        quietoDesde = 0
        return
      }
      if (quietoDesde === 0) quietoDesde = Date.now()
      if (Date.now() - quietoDesde >= 4000) {
        window.clearInterval(reloj)
        recargar()
      }
    }, 1500)
  }

  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (recargando || pendiente) return
    if (!habiaControlador) return // primera instalación, no una versión nueva

    if (!ocupado()) {
      recargar()
      return
    }
    pendiente = true
    toast.show(
      window.location.pathname.startsWith('/capturar')
        ? 'Hay una versión nueva. Se aplicará al salir de la cámara.'
        : 'Hay una versión nueva. Se aplicará cuando termines.',
    )
    vigilar()
  })

  // Si se va de la app con la versión pendiente y nada a medias, se recarga
  // mientras no mira: al volver ya tiene la nueva sin haber visto el salto.
  document.addEventListener('visibilitychange', () => {
    if (pendiente && document.visibilityState === 'hidden' && !ocupado()) recargar()
  })

  updateSW = registerSW({
    immediate: true,
    onRegisteredSW(_url, registration) {
      if (!registration) return
      // iOS restores a PWA frozen exactly as it was — days-old code included —
      // so checking only at launch is not enough. Ask for news every time the
      // app returns to the foreground, and hourly while it stays open. The
      // server sends /sw.js with no-cache, so a check always sees the truth.
      const check = () => void registration.update().catch(() => undefined)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') check()
      })
      setInterval(check, 60 * 60 * 1000)
    },
    onNeedRefresh() {
      // El aviso de verdad lo da controllerchange, que sabe si hay algo a medias.
    },
    onOfflineReady() {
      // Silent: nobody needs a notice that things are working.
    },
    onRegisterError(error: unknown) {
      console.warn('[catgo] service worker registration failed', error)
    },
  })
}

export async function applyUpdate() {
  await updateSW?.(true)
}
